import * as reactSpring from 'react-spring';

/**
 * Server-side animation frame driver for react-spring v8 (PRNT-4632).
 *
 * react-spring v8 keeps a module-level `Set` of active animation controllers, drained only
 * by `requestAnimationFrame` ticks — and its default `requestFrame` is a no-op when `window`
 * is undefined. Any spring that starts during SSR is therefore parked in that Set forever,
 * pinning the entire render graph it belongs to (React tree, props, contexts, the request's
 * i18n instance, and its New Relic transaction). Prod heap-snapshot analysis found 62 such
 * retained request graphs on a 6.8h-old charm pod (~1MB each, ~9/hour) — the residual memory
 * leak behind charm's slow post-restart climb.
 *
 * Driving frames with a timer lets server-started springs tick to completion (a few hundred
 * ms of ~30fps timers, negligible CPU), after which the frame loop deletes the controller
 * (`controllers.delete` + `active = false` in v8's update loop) and the graph becomes
 * collectible. Timers are unref'd so they never hold the process open.
 *
 * The caller must live in the ssrApp bundle (main/App.tsx calls this at module scope): the
 * leaked Set lives in the app bundle's react-spring instance, and the server bundle's copy
 * is a different module instance that never starts springs. In the browser `window` is
 * defined, so this is a no-op for CSR bundles that share App.tsx.
 *
 * react-spring v9 replaces this with `Globals.assign({ skipAnimation: true })` — if we ever
 * upgrade, delete this module in favor of that.
 */

// `Globals` is a runtime export of react-spring v8 with no TypeScript declaration.
type SpringGlobals = Readonly<{
  injectFrame: (raf: (cb: () => void) => unknown, caf: (id: unknown) => void) => void;
}>;

export const SERVER_FRAME_INTERVAL_MS = 32; // ~30fps: fast enough to finish springs promptly, cheap enough to be free

export const installServerAnimationFrameDriver = (
  globals: SpringGlobals = (reactSpring as unknown as { Globals: SpringGlobals }).Globals,
  isServer: boolean = typeof window === 'undefined',
): boolean => {
  if (!isServer) {
    return false;
  }
  globals.injectFrame(
    cb => {
      const id = setTimeout(cb, SERVER_FRAME_INTERVAL_MS);
      (id as { unref?: () => void }).unref?.();
      return id;
    },
    id => clearTimeout(id as ReturnType<typeof setTimeout>),
  );
  return true;
};
